import isHotkey from 'is-hotkey';
import { castArray } from 'lodash';
import { Editor, Point, Range, Text, Transforms } from 'slate';

import { someNode } from '../../../slatePlugins/common/queries/someNode';
import { GetOnHotkeyToggleNodeTypeOptions } from '../../../slatePlugins/common/utils/getOnHotkeyToggleNodeType';
import { setDefaults } from '../../../slatePlugins/common/utils/setDefaults';
import { unwrapNodes } from '../../../slatePlugins/transforms/unwrapNodes';
import { wrapNodes } from '../../../slatePlugins/transforms/wrapNodes';

const isBlockType = (node, type) => node.type === type;

export const toggleBlockAtSelection = (editor: Editor, type, extraProps?) => {
  if (someNode(editor, { match: { type } as any })) {
    unwrapBlockAtSelection(editor, type);
  } else {
    wrapBlockAtSelection(editor, type, extraProps);
  }
};
export const wrapBlockAtSelection = (editor, type, extraProps?, options?) => {
  const at = options?.at || editor.selection;
  if (Range.isExpanded(at)) {
    wrapNodes(
      editor,
      {
        type,
        children: [{ text: '' }],
        ...extraProps,
      },
      {
        at: at as any,
        split: true,
        ...options,
      }
    );
  } else {
    Transforms.insertNodes(
      editor,
      {
        type,
        children: [{ text: ' ' }, { text: ' ' }],
        ...extraProps,
      },
      {
        at,
        select: true,
        mode: 'lowest',
        ...options,
      }
    );
    Transforms.move(editor, { distance: 1, reverse: true, unit: 'character' });
  }
};

export const unwrapBlockAtSelection = (editor, type) => {
  let at = editor.selection;
  if (Range.isRange(at)) {
    at = Editor.unhangRange(editor, at);
  }
  const rangeRef = Editor.rangeRef(editor, at, { affinity: 'inward' });
  const [start, end] = Range.edges(at);
  const splitMode = 'highest';
  const match = (node) => Text.isText(node) || isBlockType(node, type);
  const voids = false;
  Transforms.splitNodes(editor, {
    at: end,
    match,
    mode: splitMode,
    voids,
  });

  // don't split the same node
  if (!Point.equals(start, end)) {
    Transforms.splitNodes(editor, {
      at: start,
      match,
      mode: splitMode,
      voids,
    });
  }
  at = rangeRef.unref();
  Transforms.select(editor, at);
  unwrapNodes(editor, { match: { type } as any, at: editor.selection });
};

interface GetOnHotkeyToggleNodeTypeDefaultOptions {
  key: string;
  defaultOptions: Record<string, any>;
  options?: any;
}

export const getOnHotkeyToggleNodeTypeDefault = ({
  key,
  defaultOptions,
  options,
}: GetOnHotkeyToggleNodeTypeDefaultOptions) => {
  const keyOptions = setDefaults(options, defaultOptions)[key];

  return getOnHotkeyToggleNodeType(keyOptions);
};

export const getOnHotkeyToggleNodeType = ({
  type,
  hotkey,
  ...keyOptions
}: GetOnHotkeyToggleNodeTypeOptions & { extraProps: any }) => {
  if (!hotkey) return;

  const hotkeys = castArray(hotkey);

  // eslint-disable-next-line consistent-return
  return (e: any, editor: Editor) => {
    for (const key of hotkeys) {
      if (isHotkey(key, e)) {
        e.preventDefault();
        toggleBlockAtSelection(editor, type, keyOptions.extraProps);
        return;
      }
    }
  };
};
